import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TextField } from '../../../components/TextField';
import { TextArea } from '../../../components/TextArea';
import { Select } from '../../../components/Select';
import { Button } from '../../../components/Button';
import { Label } from '../../../components/Label';
import { ImageUpload } from '../../../components/ImageUpload';
import { Loader } from '../../../components/Loader';
import { Image } from '../../../api/common';
import {
  BusinessOnboardingService,
  PromotionPayload,
  ImageResponse,
  ImagePayload,
} from '../../../api/generated';
import {
  listBusinessCategories,
  BusinessCategory,
} from '../../../api/businessCategories';
import {
  listPaymentProviders,
  PaymentProvider,
} from '../../../api/paymentProviders';
import { uploadImage, getImage } from '../../../api/images';
import { PromotionModal } from '../components/PromotionModal';
import { formatPromotionText } from '../utils/formatPromotion';
import { hasOverlappingPromotions } from '../utils/promotionOverlap';
import { SortableImageGallery } from '../components/SortableImageGallery';
import { SubmitApplicationModal } from '../components/SubmitApplicationModal';
import { Toast } from '../../../components/Toast';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/x.svg';
import { ReactComponent as ExternalLinkIcon } from '../../../assets/icons/external-link.svg';
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrow-right.svg';
import { ROUTES } from '../../../config/routes';
import { appSettings } from '../../../config/appSettings';

const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_BASE_URL || 'http://localhost:3000';

interface DraftBusinessForm {
  companyId: string;
  name: string;
  description: string;
  businessCategoryIds: string[];
  paymentProviderIds: string[];
  businessLink: string;
  logo: Image | null;
  mainImage: Image | null;
  additionalImages: Image[];
  promotions: PromotionPayload[];
}

const initialFormState: DraftBusinessForm = {
  companyId: '',
  name: '',
  description: '',
  businessCategoryIds: [],
  paymentProviderIds: [],
  businessLink: '',
  logo: null,
  mainImage: null,
  additionalImages: [],
  promotions: [],
};

interface LocationState {
  successMessage?: string;
}

// Helper function to convert ImageResponse from API to local Image type
const imageResponseToImage = (response: ImageResponse): Image => ({
  id: response.url || '', // Use URL as unique identifier
  title: response.title || '',
  url: response.url || '',
  contentType: response.contentType || '',
  width: response.width || 0,
  height: response.height || 0,
});

// Helper function to convert local Image to ImagePayload for API
const imageToPayload = (image: Image): ImagePayload => ({
  url: image.url,
  width: image.width,
  height: image.height,
  title: image.title,
  contentType: image.contentType,
});

const EditBusinessDraftPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState<DraftBusinessForm>(initialFormState);
  const [categories, setCategories] = useState<BusinessCategory[]>([]);
  const [paymentProviders, setPaymentProviders] = useState<PaymentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [editingPromotionIndex, setEditingPromotionIndex] = useState<number | null>(null);
  const [businessLinkError, setBusinessLinkError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    type: 'logo' | 'mainImage' | 'additionalImage' | 'promotion' | null;
    imageId?: string;
    promotionIndex?: number;
  }>({ isOpen: false, type: null });

  const lastSavedFormRef = useRef<DraftBusinessForm | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const AUTO_SAVE_DELAY = 2000; // 2 seconds debounce

  // Handle success message from navigation state (e.g., after creating a new draft)
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.successMessage) {
      setSuccessMessage(state.successMessage);
      // Clear the location state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No draft ID provided');
        setLoading(false);
        return;
      }

      try {
        const [categoriesData, providersData, draftData] = await Promise.all([
          listBusinessCategories(),
          listPaymentProviders(),
          BusinessOnboardingService.getApiBusinessOnboardingGetDraftBusinessApplication(id),
        ]);

        setCategories(categoriesData);
        setPaymentProviders(providersData);

        // Convert image DTOs to local Image type
        const logo = draftData.logoImage
          ? imageResponseToImage(draftData.logoImage)
          : null;
        const mainImage = draftData.mainImage
          ? imageResponseToImage(draftData.mainImage)
          : null;
        const additionalImages = (draftData.additionalImages || []).map(
          imageResponseToImage
        );

        const loadedForm: DraftBusinessForm = {
          companyId: draftData.companyId || '',
          name: draftData.name || '',
          description: draftData.description || '',
          businessCategoryIds: draftData.businessCategoryIds || [],
          paymentProviderIds: draftData.paymentProviderIds || [],
          businessLink: draftData.businessLink || '',
          logo,
          mainImage,
          additionalImages,
          promotions: (draftData.promotions || []) as PromotionPayload[],
        };
        setForm(loadedForm);
        lastSavedFormRef.current = loadedForm;
      } catch (err: any) {
        console.error('Failed to fetch data:', err);
        setError(
          err?.body?.message ||
          err?.message ||
          'Failed to load draft. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Helper to compare forms for dirty check
  const isFormDirty = useCallback(
    (currentForm: DraftBusinessForm, savedForm: DraftBusinessForm | null): boolean => {
      if (!savedForm) return false;
      return JSON.stringify(currentForm) !== JSON.stringify(savedForm);
    },
    []
  );

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (!id || saving) return;

    setSaving(true);
    setError(null);

    try {
      await BusinessOnboardingService.putApiBusinessOnboardingUpdateDraftBusinessApplication(
        id,
        {
          name: form.name || null,
          description: form.description || null,
          businessCategoryIds: form.businessCategoryIds,
          paymentProviderIds: form.paymentProviderIds,
          businessLink: form.businessLink || null,
          logoImage: form.logo ? imageToPayload(form.logo) : undefined,
          mainImage: form.mainImage ? imageToPayload(form.mainImage) : undefined,
          additionalImages: form.additionalImages.map(imageToPayload),
          promotions: form.promotions,
        }
      );
      lastSavedFormRef.current = form;
      setIsDirty(false);
    } catch (err: any) {
      setError(
        err?.body?.message ||
        err?.message ||
        'Failed to auto-save draft. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  }, [id, form, saving]);

  // Track dirty state and trigger auto-save with debounce
  useEffect(() => {
    if (loading || !lastSavedFormRef.current) return;

    const dirty = isFormDirty(form, lastSavedFormRef.current);
    setIsDirty(dirty);

    if (dirty) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new debounced auto-save
      autoSaveTimeoutRef.current = setTimeout(() => {
        performAutoSave();
      }, AUTO_SAVE_DELAY);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [form, loading, isFormDirty, performAutoSave]);

  const validateBusinessLink = (url: string): string | null => {
    if (!url) return null; // Empty is valid (not required)
    if (!url.startsWith('https://')) {
      return 'URL must start with https://';
    }
    try {
      const parsed = new URL(url);
      // Check for valid domain with at least one dot (e.g., example.com)
      const hostname = parsed.hostname;
      if (!hostname.includes('.') || hostname.split('.').some(part => part.length === 0)) {
        return 'Please enter a valid URL (e.g., https://example.com)';
      }
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'businessLink') {
      setBusinessLinkError(validateBusinessLink(value));
    }
  };

  const handleMultiSelect =
    (field: keyof DraftBusinessForm) => (value: string | string[]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleImageUpload =
    (field: 'logo' | 'mainImage') => async (file: File | null) => {
      if (!file) return;
      try {
        const uploadResult = await uploadImage(file);
        const newImage = await getImage(uploadResult.id);
        setForm((prev) => ({ ...prev, [field]: newImage }));
      } catch (error) {
        console.error('Error uploading image:', error);
        setError('Failed to upload image');
      }
    };

  const handleRemoveImage = (field: 'logo' | 'mainImage') => () => {
    setDeleteConfirmation({
      isOpen: true,
      type: field,
    });
  };

  const handleRequestDeleteAdditionalImage = (image: Image) => {
    setDeleteConfirmation({
      isOpen: true,
      type: 'additionalImage',
      imageId: image.id,
    });
  };

  const handleAddAdditionalImage = async (file: File) => {
    const uploadResult = await uploadImage(file);
    const newImage = await getImage(uploadResult.id);
    setForm((prev) => ({
      ...prev,
      additionalImages: [...prev.additionalImages, newImage],
    }));
  };

  const handleAdditionalImagesChange = (images: Image[]) => {
    setForm((prev) => ({ ...prev, additionalImages: images }));
  };

  const handleOpenAddPromotion = () => {
    setEditingPromotionIndex(null);
    setIsPromotionModalOpen(true);
  };

  const handleAddRecommendedPromotion = () => {
    const recommendedPromotion: PromotionPayload = {
      customerCashbackPercent: appSettings.DEFAULT_CUSTOMER_CASHBACK_PERCENT,
      startDateTime: null,
      endDateTime: null,
      minTimeOfDay: null,
      maxTimeOfDay: null,
      minTransactionAmount: null,
      maxTransactionAmount: null,
    };
    setForm((prev) => ({
      ...prev,
      promotions: [...prev.promotions, recommendedPromotion],
    }));
  };

  const handleOpenEditPromotion = (index: number) => {
    setEditingPromotionIndex(index);
    setIsPromotionModalOpen(true);
  };

  const handleSavePromotion = (promotion: PromotionPayload) => {
    if (editingPromotionIndex !== null) {
      // Edit existing promotion
      setForm((prev) => ({
        ...prev,
        promotions: prev.promotions.map((p, i) =>
          i === editingPromotionIndex ? promotion : p
        ),
      }));
    } else {
      // Add new promotion
      setForm((prev) => ({
        ...prev,
        promotions: [...prev.promotions, promotion],
      }));
    }
  };

  const handlePromotionDelete = (index: number) => {
    setDeleteConfirmation({
      isOpen: true,
      type: 'promotion',
      promotionIndex: index,
    });
  };

  const handleConfirmDelete = () => {
    const { type, imageId, promotionIndex } = deleteConfirmation;

    if (type === 'logo') {
      setForm((prev) => ({ ...prev, logo: null }));
    } else if (type === 'mainImage') {
      setForm((prev) => ({ ...prev, mainImage: null }));
    } else if (type === 'additionalImage' && imageId) {
      setForm((prev) => ({
        ...prev,
        additionalImages: prev.additionalImages.filter((img) => img.id !== imageId),
      }));
    } else if (type === 'promotion' && promotionIndex !== undefined) {
      setForm((prev) => ({
        ...prev,
        promotions: prev.promotions.filter((_, i) => i !== promotionIndex),
      }));
    }

    setDeleteConfirmation({ isOpen: false, type: null });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, type: null });
  };

  const getDeleteConfirmationMessage = (): { title: string; message: string } => {
    const { type } = deleteConfirmation;
    if (type === 'logo') {
      return {
        title: 'Delete Logo',
        message: 'Are you sure you want to delete the logo image? This action cannot be undone.',
      };
    } else if (type === 'mainImage') {
      return {
        title: 'Delete Main Image',
        message: 'Are you sure you want to delete the main image? This action cannot be undone.',
      };
    } else if (type === 'additionalImage') {
      return {
        title: 'Delete Image',
        message: 'Are you sure you want to delete this image? This action cannot be undone.',
      };
    } else if (type === 'promotion') {
      return {
        title: 'Delete Promotion',
        message: 'Are you sure you want to delete this promotion? This action cannot be undone.',
      };
    }
    return { title: 'Confirm Delete', message: 'Are you sure you want to delete this item?' };
  };

  const handleSaveDraft = async () => {
    if (!id) return;

    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await BusinessOnboardingService.putApiBusinessOnboardingUpdateDraftBusinessApplication(
        id,
        {
          name: form.name || null,
          description: form.description || null,
          businessCategoryIds: form.businessCategoryIds,
          paymentProviderIds: form.paymentProviderIds,
          businessLink: form.businessLink || null,
          logoImage: form.logo ? imageToPayload(form.logo) : undefined,
          mainImage: form.mainImage ? imageToPayload(form.mainImage) : undefined,
          additionalImages: form.additionalImages.map(imageToPayload),
          promotions: form.promotions,
        }
      );
      lastSavedFormRef.current = form;
      setIsDirty(false);
      setSuccessMessage('Draft saved successfully!');
    } catch (err: any) {
      setError(
        err?.body?.message ||
        err?.message ||
        'Failed to save draft. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await BusinessOnboardingService.postApiBusinessOnboardingSubmitDraftBusinessApplicationForReview(id);
      setIsSubmitModalOpen(false);
      navigate(ROUTES.PROMOTIONS(), {
        state: { successMessage: 'Your application has been submitted for review!' },
      });
    } catch (err: any) {
      setError(
        err?.body?.message ||
        err?.message ||
        'Failed to submit application. Please try again.'
      );
      setIsSubmitModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect to business registration if draft not found
  useEffect(() => {
    if (!loading && error && !form.name) {
      navigate(ROUTES.PROMOTIONS(), {
        state: { errorMessage: 'Draft not found. Please start a new registration.' },
      });
    }
  }, [loading, error, form.name, navigate]);

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  // If there's an error and no form data, the useEffect above will handle the redirect
  if (error && !form.name) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-10">
      {successMessage && (
        <Toast
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />
      )}
      <Button
        variant="ghost"
        onClick={() => navigate(ROUTES.PROMOTIONS())}
        className="mb-2 gap-2 text-gray-600 justify-start pl-0"
      >
        <ArrowRight className="w-4 h-4 rotate-180" />
        Back to Promotions
      </Button>

      <div className="mb-6">
        <h2 className="font-medium">Business and Promotion Registration</h2>
      </div>

      <div className="space-y-8">
        {/* Section 0: Registration Status */}
        <section className="bg-gray-50 rounded-2xl p-8 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-medium">Status</h3>
              <span className="inline-block px-3 py-1 text-sm font-medium text-gray-600 bg-gray-200 rounded-full">
                DRAFT{isDirty && '*'}
              </span>
              {saving && (
                <span className="text-sm text-gray-500">Saving...</span>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(`${FRONTEND_BASE_URL}/promotion-preview/card-linking/${form.companyId}/${id}`, '_blank')}
            >
              Preview
              <ExternalLinkIcon className="w-4 h-4 ml-2" />
            </Button>
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                isLoading={saving}
                disabled={saving}
              >
                Save Changes
              </Button>
              <Button
                type="button"
                onClick={() => setIsSubmitModalOpen(true)}
                disabled={saving || isDirty}
              >
                Submit for Review
              </Button>
            </div>
          </div>
        </section>

        {/* Section 1: Business Details */}
        <section className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-lg font-medium mb-6">Business Details</h3>
          <div className="grid lg:grid-cols-2 gap-x-8 gap-y-4">
            <TextField
              type="text"
              id="name"
              name="name"
              label="Business Name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <TextField
              type="url"
              id="businessLink"
              name="businessLink"
              label={<Label additionalText="(must start with https://)">Business Website</Label>}
              value={form.businessLink}
              onChange={handleChange}
              placeholder="https://example.com"
              error={businessLinkError || undefined}
            />

            <div className="lg:col-span-2">
              <TextArea
                id="description"
                name="description"
                label="Description"
                value={form.description}
                onChange={handleChange}
                className="min-h-24"
              />
            </div>

            {categories.length > 0 && (
              <Select
                multiple
                label={<Label additionalText="(You can select more than one)">Business Categories</Label>}
                value={form.businessCategoryIds}
                onChange={handleMultiSelect('businessCategoryIds')}
                values={categories.map((c) => ({ label: c.name, value: c.id }))}
              />
            )}

            {paymentProviders.length > 0 && (
              <Select
                multiple
                label={<Label additionalText="(You can select more than one)">Payment Providers</Label>}
                value={form.paymentProviderIds}
                onChange={handleMultiSelect('paymentProviderIds')}
                values={paymentProviders.map((p) => ({
                  label: p.name,
                  value: p.id,
                }))}
              />
            )}
          </div>
        </section>

        {/* Section 2: Images */}
        <section className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-lg font-medium mb-4">Images</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <ImageUpload
              images={form.logo ? [form.logo] : undefined}
              onChange={handleImageUpload('logo')}
              onDelete={handleRemoveImage('logo')}
              label={<Label additionalText="(1:1 ratio)">Logo</Label>}
              aspectRatio={1}
            />

            <ImageUpload
              images={form.mainImage ? [form.mainImage] : undefined}
              onChange={handleImageUpload('mainImage')}
              onDelete={handleRemoveImage('mainImage')}
              label={<Label additionalText="(9:10 ratio)">Main Image</Label>}
              aspectRatio={9 / 10}
            />

            <div className="md:col-span-2">
              <SortableImageGallery
                images={form.additionalImages}
                onChange={handleAdditionalImagesChange}
                onAddImage={handleAddAdditionalImage}
                onRequestDelete={handleRequestDeleteAdditionalImage}
                label={<Label additionalText="(4:3 ratio, drag to reorder)">Additional Images</Label>}
                aspectRatio={4 / 3}
              />
            </div>
          </div>
        </section>

        {/* Section 3: Promotions */}
        <section className="bg-gray-50 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium">Promotions</h3>
            <Button
              type="button"
              variant="light"
              size="sm"
              onClick={handleOpenAddPromotion}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Promotion
            </Button>
          </div>

          {hasOverlappingPromotions(form.promotions) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Note:</span> Some promotions overlap based on their constraints (date/time, time of day, or transaction amount). When multiple promotions apply, the customer will receive the promotion with the higher cashback percentage.
              </p>
            </div>
          )}

          {form.promotions.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No promotions added yet. Click "Add Promotion" to create a cashback
              offer for your customers, or{' '}
              <button
                type="button"
                onClick={handleAddRecommendedPromotion}
                className="text-primary hover:underline font-medium"
              >
                add promotion recommended by OiSTER Bonus
              </button>
              .
            </p>
          ) : (
            <ul className="space-y-3">
              {form.promotions.map((promotion, index) => (
                <li
                  key={index}
                  className="flex items-start justify-between gap-4 bg-white border border-gray-200 rounded-xl p-4"
                >
                  <span className="text-sm text-gray-700 flex-1">
                    {formatPromotionText(promotion)}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="icon"
                      onClick={() => handleOpenEditPromotion(index)}
                      className="text-gray-400 hover:text-primary"
                      title="Edit promotion"
                    >
                      <EditIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="icon"
                      onClick={() => handlePromotionDelete(index)}
                      className="text-gray-400 hover:text-red-500"
                      title="Delete promotion"
                    >
                      <DeleteIcon className="w-3 h-3" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <SubmitApplicationModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
        onSubmit={handleSubmitForReview}
        isSubmitting={isSubmitting}
        formData={{
          description: form.description,
          businessLink: form.businessLink,
          businessCategoryIds: form.businessCategoryIds,
          paymentProviderIds: form.paymentProviderIds,
          logo: form.logo,
          mainImage: form.mainImage,
          additionalImages: form.additionalImages,
          promotions: form.promotions,
        }}
      />

      <PromotionModal
        isOpen={isPromotionModalOpen}
        onClose={() => setIsPromotionModalOpen(false)}
        onSave={handleSavePromotion}
        promotion={
          editingPromotionIndex !== null
            ? form.promotions[editingPromotionIndex]
            : undefined
        }
        mode={editingPromotionIndex !== null ? 'edit' : 'add'}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={getDeleteConfirmationMessage().title}
        message={getDeleteConfirmationMessage().message}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default EditBusinessDraftPage;
